import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
  Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, CircularProgress, Alert, Chip, Tabs, Tab, Card, CardContent
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import { Save, RefreshCw, Hash, Users, Layers, Mic, FileText } from 'lucide-react';
import axiosInstance from '../axiosConfig';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#2563EB', light: '#3B82F6', dark: '#1E40AF' },
    secondary: { main: '#10B981', light: '#34D399', dark: '#047857' },
    info: { main: '#6366F1', light: '#818CF8', dark: '#4F46E5' },
    warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
    error: { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
    background: { default: '#F0F4F8', paper: '#FFFFFF' }
  },
  typography: { fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif' },
  shape: { borderRadius: 12 }
});

const DashboardContainer = styled(Box)({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
  paddingTop: 24,
  paddingBottom: 40,
});

const StyledCard = styled(Card)({
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)',
  border: '1px solid rgba(0,0,0,0.06)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1), 0 12px 32px rgba(0,0,0,0.06)',
  }
});

const StyledCardContent = styled(CardContent)({ padding: '24px !important' });

const StatWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const StatValue = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginTop: 4,
  color: '#1a202c',
});

const IconBox = styled(Box)(({ color }) => ({
  width: 52,
  height: 52,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color}22, ${color}11)`,
  border: `1px solid ${color}33`,
  color: color,
}));

const ChartCard = styled(Card)({
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)',
  border: '1px solid rgba(0,0,0,0.06)',
  padding: 24,
});

const PageHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 32,
  flexWrap: 'wrap',
  gap: 16,
});

const SECTION_CONFIG = {
  'Dominic Savio': { classes: ['IV', 'V', 'VI'], label: 'Classes IV-VI' },
  'Alphonsa': { classes: ['VII', 'VIII', 'IX'], label: 'Classes VII-IX' },
  'Saint Thomas': { classes: ['X', 'XI', 'XII'], label: 'Classes X-XII' }
};

const FORANE_ID = '673799a3cb9b4aa181e53fa2';

const TAB_COLORS = {
  onstage: { main: '#2563EB', bg: 'rgba(37,99,235,0.04)', border: 'rgba(37,99,235,0.12)' },
  offstage: { main: '#6366F1', bg: 'rgba(99,102,241,0.04)', border: 'rgba(99,102,241,0.12)' },
  group: { main: '#10B981', bg: 'rgba(16,185,129,0.04)', border: 'rgba(16,185,129,0.12)' },
};

const RegistrationNumberAssignment = () => {
  const [selectedSection, setSelectedSection] = useState('');
  const [parishes, setParishes] = useState([]);
  const [onStageParticipants, setOnStageParticipants] = useState([]);
  const [offStageParticipants, setOffStageParticipants] = useState([]);
  const [groupEntries, setGroupEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeTab, setActiveTab] = useState(0);

  const [onStageStart, setOnStageStart] = useState(1);
  const [onStageIncrement, setOnStageIncrement] = useState(1);
  const [offStageStart, setOffStageStart] = useState(1);
  const [offStageIncrement, setOffStageIncrement] = useState(1);
  const [groupStart, setGroupStart] = useState(1);
  const [groupIncrement, setGroupIncrement] = useState(1);

  const [eventStageMap, setEventStageMap] = useState({});

  useEffect(() => {
    fetchParishes();
    fetchEventStageMap();
  }, []);

  const fetchEventStageMap = async () => {
    try {
      const [eventsRes, categoriesRes] = await Promise.all([
        axiosInstance.get('/events'),
        axiosInstance.get('/categories')
      ]);

      // DEBUG: log raw responses
      console.log('--- DEBUG categories raw:', categoriesRes.data);
      console.log('--- DEBUG events raw:', eventsRes.data);

      const cats = categoriesRes.data?.data?.categories
        || categoriesRes.data?.categories
        || categoriesRes.data || [];
      const cMap = {};
      for (const c of cats) { cMap[String(c._id)] = c; }
      console.log('--- DEBUG categoryMap:', cMap);
      console.log('--- DEBUG sample category:', cats[0]);

      const evts = eventsRes.data?.data?.events
        || eventsRes.data?.events
        || eventsRes.data || [];
      console.log('--- DEBUG sample event:', evts[0]);
      console.log('--- DEBUG sample event.category:', evts[0]?.category, 'typeof:', typeof evts[0]?.category);

      const esMap = {};
      for (const e of evts) {
        let catId = '';
        if (e.category && typeof e.category === 'object' && e.category._id) {
          // category is populated object
          catId = String(e.category._id);
          // If populated, stage might be right here
          if (e.category.stage) {
            esMap[String(e._id)] = e.category.stage;
            continue;
          }
        } else {
          catId = String(e.category || '');
        }
        esMap[String(e._id)] = cMap[catId]?.stage || '';
      }
      console.log('--- DEBUG eventStageMap:', esMap);
      setEventStageMap(esMap);
    } catch (error) {
      console.error('Error fetching event stage map:', error);
    }
  };

  useEffect(() => {
    if (selectedSection && parishes.length > 0 && Object.keys(eventStageMap).length > 0)
      fetchAllRegistrations();
  }, [selectedSection, parishes, eventStageMap]);

  const fetchParishes = async () => {
    try {
      const response = await axiosInstance.get('/parish');
      const filtered = (response.data || []).filter(
        (p) => p.forane === FORANE_ID || p.forane?._id === FORANE_ID
      );
      setParishes(filtered);
    } catch (error) {
      console.error('Error fetching parishes:', error);
    }
  };

  const fetchAllRegistrations = async () => {
    try {
      setIsLoading(true);

      const results = await Promise.allSettled(
        parishes.map(parish =>
          axiosInstance.get(`/registrations/parish/${parish._id}`)
            .then(res => ({
              parishName: parish.name,
              parishId: parish._id,
              registrations: res.data.data.registrations || []
            }))
        )
      );

      const onStageMap = {};
      const offStageMap = {};
      const groupMap = {};

      let debugLogged = false;
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const { parishName, parishId, registrations } = result.value;

        for (const reg of registrations) {
          if (reg.event?.section !== selectedSection) continue;

          // DEBUG: log first registration's event shape
          if (!debugLogged) {
            console.log('--- DEBUG first reg.event:', reg.event);
            console.log('--- DEBUG reg.event._id:', reg.event?._id, 'typeof:', typeof reg.event?._id);
            console.log('--- DEBUG reg.event.category:', reg.event?.category, 'typeof:', typeof reg.event?.category);
            const testId = String(reg.event?._id || '');
            console.log('--- DEBUG lookup eventStageMap[' + testId + ']:', eventStageMap[testId]);
            debugLogged = true;
          }
          const eventType = reg.event?.eventType;

          if (eventType === 'single') {
            const dobStr = reg.dob ? new Date(reg.dob).toISOString().slice(0, 10) : '';
            const key = `${reg.name}|${reg.standard}|${reg.gender}|${dobStr}|${parishId}`;
            const evtId = typeof reg.event === 'object' ? String(reg.event?._id || '') : String(reg.event || '');
            const isOffStage = eventStageMap[evtId] === 'Off Stage';
            const targetMap = isOffStage ? offStageMap : onStageMap;
            const stageKey = `${key}|${isOffStage ? 'off' : 'on'}`;

            if (!targetMap[stageKey]) {
              targetMap[stageKey] = {
                name: reg.name, standard: reg.standard, gender: reg.gender,
                dob: reg.dob, parish: parishName, parishId,
                registrationNumber: reg.registrationNumber || '',
                events: [], registrationIds: []
              };
            }
            targetMap[stageKey].events.push({ eventName: reg.event.eventName });
            targetMap[stageKey].registrationIds.push(reg._id);
          } else if (eventType === 'group') {
            const groupKey = `${parishId}|${reg.event._id}`;
            if (!groupMap[groupKey]) {
              groupMap[groupKey] = {
                parish: parishName, parishId, eventName: reg.event.eventName,
                groupRegistrationNumber: reg.groupRegistrationNumber || '',
                participantCount: 0, registrationIds: []
              };
            }
            groupMap[groupKey].participantCount++;
            groupMap[groupKey].registrationIds.push(reg._id);
          }
        }
      }

      const sortFn = (a, b) => a.parish.localeCompare(b.parish) || a.name.localeCompare(b.name);
      setOnStageParticipants(Object.values(onStageMap).sort(sortFn));
      setOffStageParticipants(Object.values(offStageMap).sort(sortFn));
      setGroupEntries(
        Object.values(groupMap).sort((a, b) =>
          a.parish.localeCompare(b.parish) || a.eventName.localeCompare(b.eventName)
        )
      );
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const autoAssign = useCallback((setter, start, increment) => {
    setter(prev => prev.map((p, i) => ({
      ...p,
      registrationNumber: String(start + i * increment)
    })));
  }, []);

  const autoAssignGroupNumbers = useCallback(() => {
    setGroupEntries(prev =>
      prev.map((g, i) => ({ ...g, groupRegistrationNumber: String(groupStart + i * groupIncrement) }))
    );
  }, [groupStart, groupIncrement]);

  const updateRegNo = useCallback((setter, index, value) => {
    setter(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], registrationNumber: value };
      return updated;
    });
  }, []);

  const updateGroupRegNo = useCallback((index, value) => {
    setGroupEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], groupRegistrationNumber: value };
      return updated;
    });
  }, []);

  const batchSave = async (items, field) => {
    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;

    const tasks = [];
    for (const item of items) {
      const value = item[field];
      if (!value) continue;
      for (const regId of item.registrationIds) {
        tasks.push({ regId, payload: { [field]: value } });
      }
    }

    const BATCH_SIZE = 10;
    for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
      const batch = tasks.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(t => axiosInstance.put(`/registrations/${t.regId}`, t.payload))
      );
      for (const r of results) {
        if (r.status === 'fulfilled') successCount++;
        else errorCount++;
      }
    }

    setMessage({
      text: `${successCount} updated${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
      type: errorCount > 0 ? 'warning' : 'success'
    });
    setIsSaving(false);
  };

  const busy = isLoading || isSaving;

  const allParticipants = [...onStageParticipants, ...offStageParticipants];
  const uniqueParishes = new Set(
    allParticipants.map(p => p.parish).concat(groupEntries.map(g => g.parish))
  ).size;

  const statisticsCards = [
    { title: 'On Stage', value: onStageParticipants.length, color: TAB_COLORS.onstage.main, icon: <Mic size={24} /> },
    { title: 'Off Stage', value: offStageParticipants.length, color: TAB_COLORS.offstage.main, icon: <FileText size={24} /> },
    { title: 'Group Entries', value: groupEntries.length, color: TAB_COLORS.group.main, icon: <Layers size={24} /> },
    { title: 'Parishes', value: uniqueParishes, color: '#F59E0B', icon: <Hash size={24} /> },
  ];

  /* ---- Reusable single-participant table ---- */
  const renderSingleTable = (items, setter, start, setStart, increment, setIncrement, colorKey, label) => {
    const colors = TAB_COLORS[colorKey];
    return (
      <>
        <Box sx={{ p: 2.5, mb: 3, borderRadius: 3, bgcolor: colors.bg, border: `1px solid ${colors.border}` }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1a202c' }}>
            Auto-Assign Settings ({label})
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Starting Number"
                value={start} onChange={(e) => setStart(Number(e.target.value))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Increment"
                value={increment} onChange={(e) => setIncrement(Number(e.target.value))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button variant="outlined" fullWidth onClick={() => autoAssign(setter, start, increment)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 40, color: colors.main, borderColor: colors.main }}>
                Auto Assign
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button variant="contained" fullWidth startIcon={<Save size={18} />}
                onClick={() => batchSave(items, 'registrationNumber')} disabled={busy}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 40,
                  bgcolor: colors.main, boxShadow: `0 2px 8px ${colors.main}4D`,
                  '&:hover': { bgcolor: colors.main }
                }}>
                Save {label}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell sx={{ fontWeight: 600 }}>No.</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reg. Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Parish</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Events</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p, index) => (
                <TableRow key={`${p.parishId}-${p.name}-${index}`}
                  sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField size="small" variant="outlined"
                      value={p.registrationNumber}
                      onChange={(e) => updateRegNo(setter, index, e.target.value)}
                      sx={{ width: 120 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{p.name}</TableCell>
                  <TableCell>{p.standard}</TableCell>
                  <TableCell>{p.gender === 'M' ? 'Male' : 'Female'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={p.parish} sx={{
                      bgcolor: `${colors.main}15`, color: colors.main,
                      border: `1px solid ${colors.main}30`, fontWeight: 600
                    }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {p.events.map((e, i) => (
                        <Chip key={i} size="small" label={e.eventName}
                          variant="outlined" sx={{ fontSize: '0.75rem' }} />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Users size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
                    <Typography color="textSecondary">
                      No {label.toLowerCase()} participants found in this section
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <DashboardContainer>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
              <PageHeader>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a202c', mb: 0.5 }}>
                    Registration Number Assignment
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Assign registration numbers for Forane Kalolsavam participants
                  </Typography>
                </Box>
                {selectedSection && (
                  <Button variant="outlined" startIcon={<RefreshCw size={18} />}
                    onClick={fetchAllRegistrations} disabled={busy}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                  </Button>
                )}
              </PageHeader>
            </Grid>

            {/* Message */}
            {message.text && (
              <Grid item xs={12}>
                <Alert severity={message.type} sx={{ borderRadius: 2 }}
                  onClose={() => setMessage({ text: '', type: '' })}>
                  {message.text}
                </Alert>
              </Grid>
            )}

            {/* Section Selector */}
            <Grid item xs={12} sm={6} md={3}>
              <StyledCard>
                <StyledCardContent>
                  <FormControl fullWidth>
                    <InputLabel>Select Section</InputLabel>
                    <Select value={selectedSection} label="Select Section"
                      onChange={(e) => setSelectedSection(e.target.value)}>
                      <MenuItem value="">Select Section</MenuItem>
                      {Object.entries(SECTION_CONFIG).map(([section, config]) => (
                        <MenuItem key={section} value={section}>
                          {section} ({config.label})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </StyledCardContent>
              </StyledCard>
            </Grid>

            {/* Stat Cards */}
            {selectedSection && !busy && statisticsCards.map((stat, index) => (
              <Grid item xs={6} sm={3} md={2.25} key={index}>
                <StyledCard>
                  <StyledCardContent>
                    <StatWrapper>
                      <Box>
                        <Typography variant="subtitle2" sx={{
                          color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.1em'
                        }}>{stat.title}</Typography>
                        <StatValue sx={{ fontSize: '1.75rem' }}>{stat.value}</StatValue>
                      </Box>
                      <IconBox color={stat.color}>{stat.icon}</IconBox>
                    </StatWrapper>
                  </StyledCardContent>
                </StyledCard>
              </Grid>
            ))}

            {/* Main Content */}
            {busy ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : selectedSection ? (
              <Grid item xs={12}>
                <ChartCard>
                  <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}
                    sx={{ mb: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Tab label={`On Stage (${onStageParticipants.length})`}
                      icon={<Mic size={16} />} iconPosition="start"
                      sx={{ textTransform: 'none', fontWeight: 600 }} />
                    <Tab label={`Off Stage (${offStageParticipants.length})`}
                      icon={<FileText size={16} />} iconPosition="start"
                      sx={{ textTransform: 'none', fontWeight: 600 }} />
                    <Tab label={`Group (${groupEntries.length})`}
                      icon={<Layers size={16} />} iconPosition="start"
                      sx={{ textTransform: 'none', fontWeight: 600 }} />
                  </Tabs>

                  {/* On Stage Tab */}
                  {activeTab === 0 && renderSingleTable(
                    onStageParticipants, setOnStageParticipants,
                    onStageStart, setOnStageStart, onStageIncrement, setOnStageIncrement,
                    'onstage', 'On Stage'
                  )}

                  {/* Off Stage Tab */}
                  {activeTab === 1 && renderSingleTable(
                    offStageParticipants, setOffStageParticipants,
                    offStageStart, setOffStageStart, offStageIncrement, setOffStageIncrement,
                    'offstage', 'Off Stage'
                  )}

                  {/* Group Tab */}
                  {activeTab === 2 && (
                    <>
                      <Box sx={{
                        p: 2.5, mb: 3, borderRadius: 3,
                        bgcolor: TAB_COLORS.group.bg, border: `1px solid ${TAB_COLORS.group.border}`
                      }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1a202c' }}>
                          Auto-Assign Settings (Group)
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth size="small" type="number" label="Starting Number"
                              value={groupStart} onChange={(e) => setGroupStart(Number(e.target.value))} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField fullWidth size="small" type="number" label="Increment"
                              value={groupIncrement} onChange={(e) => setGroupIncrement(Number(e.target.value))} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Button variant="outlined" fullWidth onClick={autoAssignGroupNumbers}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 40,
                                color: TAB_COLORS.group.main, borderColor: TAB_COLORS.group.main }}>
                              Auto Assign
                            </Button>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Button variant="contained" fullWidth startIcon={<Save size={18} />}
                              onClick={() => batchSave(groupEntries, 'groupRegistrationNumber')} disabled={busy}
                              sx={{
                                borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 40,
                                bgcolor: TAB_COLORS.group.main,
                                boxShadow: `0 2px 8px ${TAB_COLORS.group.main}4D`,
                                '&:hover': { bgcolor: TAB_COLORS.group.main }
                              }}>
                              Save Group
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>

                      <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                              <TableCell sx={{ fontWeight: 600 }}>No.</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Group Reg. Number</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Parish</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Event Name</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Participants</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {groupEntries.map((g, index) => (
                              <TableRow key={`${g.parishId}-${g.eventName}-${index}`}
                                sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                  <TextField size="small" variant="outlined"
                                    value={g.groupRegistrationNumber}
                                    onChange={(e) => updateGroupRegNo(index, e.target.value)}
                                    sx={{ width: 120 }} />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={g.parish} sx={{
                                    bgcolor: `${TAB_COLORS.group.main}15`, color: TAB_COLORS.group.main,
                                    border: `1px solid ${TAB_COLORS.group.main}30`, fontWeight: 600
                                  }} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{g.eventName}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={g.participantCount}
                                    sx={{ bgcolor: 'rgba(0,0,0,0.06)', fontWeight: 600 }} />
                                </TableCell>
                              </TableRow>
                            ))}
                            {groupEntries.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                  <Layers size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
                                  <Typography color="textSecondary">
                                    No group entries found in this section
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </ChartCard>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <ChartCard>
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Hash size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
                    <Typography color="textSecondary" sx={{ fontSize: '1.05rem' }}>
                      Select a section to view and assign registration numbers
                    </Typography>
                  </Box>
                </ChartCard>
              </Grid>
            )}
          </Grid>
        </Container>
      </DashboardContainer>
    </ThemeProvider>
  );
};

export default RegistrationNumberAssignment;